import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Home, Menu, X, LogIn, LogOut, User, Sparkles, Music2, Mic,
  CalendarDays, Users, Upload, ListMusic, ChevronDown,
  Store, MessageSquare, LayoutDashboard, Download, Smartphone, Cannabis,
  RefreshCw, Truck, Heart, Info, Building2, ClipboardList, CreditCard
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';
import { usePlayer } from '@/context/PlayerContext';
import { toast } from 'sonner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';

import NotificationBell from '@/components/NotificationBell';

const Sidebar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [musicOpen, setMusicOpen] = useState(false);
  const [communityOpen, setCommunityOpen] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const location = useLocation();
  const { user, login, logout, loading } = useAuth();
  const { currentTrack } = usePlayer();

  // Auto-expand section if current path is in it
  useEffect(() => {
    const musicPaths = ['/for-you', '/playlists', '/register/band'];
    const communityPaths = ['/feed', '/vendors', '/food-trucks', '/canna-club', '/submit'];
    if (musicPaths.some(p => location.pathname === p)) setMusicOpen(true);
    if (communityPaths.some(p => location.pathname === p)) setCommunityOpen(true);
  }, [location.pathname]);

  // Fetch pending admin counts
  useEffect(() => {
    if (user?.role !== 'admin') return;
    const adminKey = localStorage.getItem('adminKey');
    if (!adminKey) return;
    const fetchPending = async () => {
      try {
        const res = await fetch('/api/admin/stats', { headers: { 'X-Admin-Key': adminKey } });
        if (!res.ok) return;
        const data = await res.json();
        const regs = data.pending_registrations || {};
        const content = data.pending_content || {};
        const total = Object.values(regs).reduce((s, v) => s + (v || 0), 0)
          + Object.values(content).reduce((s, v) => s + (v || 0), 0);
        setPendingCount(total);
      } catch {}
    };
    fetchPending();
    const interval = setInterval(fetchPending, 60000);
    return () => clearInterval(interval);
  }, [user]);

  useEffect(() => {
    const checkInstalled = () => {
      const isStandalone = window.matchMedia('(display-mode: standalone)').matches 
        || window.navigator.standalone 
        || document.referrer.includes('android-app://');
      setIsInstalled(isStandalone);
    };
    checkInstalled();

    const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(isIOSDevice);

    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    window.addEventListener('appinstalled', () => setIsInstalled(true));

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    };
  }, []);

  const handleInstallClick = async () => {
    if (isIOS) {
      setShowIOSInstructions(true);
      return;
    }
    if (deferredPrompt) {
      try {
        deferredPrompt.prompt();
        const { outcome } = await deferredPrompt.userChoice;
        if (outcome === 'accepted') {
          setIsInstalled(true);
          toast.success('App installed successfully!');
        }
        setDeferredPrompt(null);
      } catch (err) {
        console.error('Install prompt error:', err);
        toast.error('Installation failed. Try using your browser menu to "Add to Home Screen"');
      }
    } else if (window.deferredInstallPrompt) {
      try {
        window.deferredInstallPrompt.prompt();
        const { outcome } = await window.deferredInstallPrompt.userChoice;
        if (outcome === 'accepted') {
          setIsInstalled(true);
          toast.success('App installed successfully!');
        }
        window.deferredInstallPrompt = null;
      } catch (err) {
        console.error('Install prompt error:', err);
        showManualInstallInstructions();
      }
    } else {
      showManualInstallInstructions();
    }
  };

  const showManualInstallInstructions = () => {
    const isChrome = /chrome/i.test(navigator.userAgent);
    const isSamsung = /samsungbrowser/i.test(navigator.userAgent);
    const isFirefox = /firefox/i.test(navigator.userAgent);
    
    if (isChrome || isSamsung) {
      toast.info(
        <div>
          <p className="font-bold">To install Twin Tube:</p>
          <ol className="mt-2 text-sm list-decimal pl-4">
            <li>Tap the <strong>3 dots</strong> menu</li>
            <li>Tap <strong>"Add to Home Screen"</strong> or <strong>"Install app"</strong></li>
          </ol>
        </div>,
        { duration: 10000 }
      );
    } else if (isFirefox) {
      toast.info(
        <div>
          <p className="font-bold">To install Twin Tube:</p>
          <ol className="mt-2 text-sm list-decimal pl-4">
            <li>Tap the <strong>3 dots</strong> menu</li>
            <li>Tap <strong>"Install"</strong></li>
          </ol>
        </div>,
        { duration: 10000 }
      );
    } else {
      toast.info('Open this site in Chrome or Samsung Internet to install the app', { duration: 5000 });
    }
  };

  const handleForceUpdate = async () => {
    setIsUpdating(true);
    try {
      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        for (const registration of registrations) {
          await registration.unregister();
        }
      }
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(cacheNames.map(name => caches.delete(name)));
      }
      toast.success('App updated! Reloading...');
      setTimeout(() => window.location.reload(true), 1000);
    } catch (error) {
      toast.error('Update failed. Try refreshing manually.');
      setIsUpdating(false);
    }
  };

  const isActive = (path) => location.pathname === path;

  const NavLink = ({ path, label, icon: Icon, indent }) => (
    <Link
      to={path}
      onClick={() => setIsOpen(false)}
      data-testid={`nav-${label.toLowerCase().replace(/\s+/g, '-')}`}
      className={`flex items-center gap-3 py-2 rounded-lg transition-all ${
        indent ? 'pl-9 pr-3' : 'px-3'
      } ${
        isActive(path)
          ? 'bg-purple-500/20 text-purple-400'
          : 'text-gray-400 hover:text-white hover:bg-white/5'
      }`}
    >
      <Icon size={indent ? 15 : 18} />
      <span className={`font-medium ${indent ? 'text-[13px]' : 'text-sm'}`}>{label}</span>
    </Link>
  );

  const SectionToggle = ({ label, icon: Icon, isExpanded, onToggle }) => (
    <button
      onClick={onToggle}
      data-testid={`nav-section-${label.toLowerCase()}`}
      className={`flex items-center justify-between w-full px-3 py-2 rounded-lg transition-all ${
        isExpanded ? 'text-white' : 'text-gray-400 hover:text-white hover:bg-white/5'
      }`}
    >
      <span className="flex items-center gap-3">
        <Icon size={18} />
        <span className="text-sm font-medium">{label}</span>
      </span>
      <ChevronDown
        size={14}
        className={`text-gray-500 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
      />
    </button>
  );

  return (
    <>
      {/* Mobile menu button */}
      {!isOpen && (
        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setIsOpen(true)}
          className="lg:hidden fixed top-4 left-4 z-50 p-2 rounded-lg bg-card/90 backdrop-blur border border-border"
        >
          <Menu size={22} />
        </button>
      )}

      {/* Overlay */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setIsOpen(false)}
            className="lg:hidden fixed inset-0 bg-black/70 backdrop-blur-sm z-40"
          />
        )}
      </AnimatePresence>

      {/* Sidebar */}
      <aside
        className={`fixed left-0 top-0 h-full w-60 bg-card border-r border-border z-40 flex flex-col transition-transform duration-300 ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        } lg:translate-x-0 ${currentTrack ? 'pb-20' : ''}`}
      >
        {/* Header with Logo & Close */}
        <div className="p-4 border-b border-border">
          <div className="flex items-center justify-between">
            <Link to="/" onClick={() => setIsOpen(false)} className="flex-1">
              <div className="relative overflow-hidden rounded-lg border border-purple-500/30">
                <div 
                  className="absolute inset-0 bg-cover bg-center opacity-30"
                  style={{ backgroundImage: 'url(/images/twin-tube-bg.jpg)' }}
                />
                <div className="absolute inset-0 bg-gradient-to-b from-black/60 to-black/80" />
                <div className="relative px-3 py-2">
                  <h1 className="font-heading text-xl font-bold tracking-tight">
                    <span className="text-purple-400">TWIN</span>
                    <span className="text-blue-400">TUBE</span>
                  </h1>
                  <p className="text-[9px] text-purple-300/80 font-medium tracking-wide">Support Good Vibes</p>
                  <p className="text-[8px] text-gray-400 tracking-wide uppercase">Twin Cities Creative Network</p>
                </div>
              </div>
            </Link>
            
            <button
              onClick={() => setIsOpen(false)}
              className="lg:hidden ml-2 p-1.5 rounded-lg text-gray-400 hover:text-white hover:bg-white/10"
              aria-label="Close menu"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-3 overflow-y-auto space-y-1">
          {/* Home */}
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <NavLink path="/" label="Home" icon={Home} />
            </div>
            <NotificationBell />
          </div>

          {/* Music Section */}
          <SectionToggle
            label="Music"
            icon={Music2}
            isExpanded={musicOpen}
            onToggle={() => setMusicOpen(!musicOpen)}
          />
          <AnimatePresence initial={false}>
            {musicOpen && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="overflow-hidden"
              >
                <div className="space-y-0.5 pb-1">
                  <NavLink path="/music" label="Browse" icon={Music2} indent />
                  <NavLink path="/for-you" label="For You" icon={Sparkles} indent />
                  <NavLink path="/playlists" label="Playlists" icon={ListMusic} indent />
                  <NavLink path="/register/band" label="Artist Registration" icon={Mic} indent />
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Events */}
          <NavLink path="/calendar" label="Events" icon={CalendarDays} />

          {/* Venues */}
          <NavLink path="/venues" label="Venues" icon={Building2} />
          <NavLink path="/venue-dashboard" label="Venue Dashboard" icon={LayoutDashboard} indent />

          {/* Community Section */}
          <SectionToggle
            label="Community"
            icon={Users}
            isExpanded={communityOpen}
            onToggle={() => setCommunityOpen(!communityOpen)}
          />
          <AnimatePresence initial={false}>
            {communityOpen && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="overflow-hidden"
              >
                <div className="space-y-0.5 pb-1">
                  <NavLink path="/feed" label="Social" icon={Heart} indent />
                  <NavLink path="/network" label="My Network" icon={Users} indent />
                  <NavLink path="/community" label="Creator Lounge" icon={ClipboardList} indent />
                  <NavLink path="/vendors" label="Shop Local" icon={Store} indent />
                  <NavLink path="/food-trucks" label="Food Trucks" icon={Truck} indent />
                  <NavLink path="/register/venue" label="Register Venue" icon={Building2} indent />
                  <NavLink path="/canna-club" label="Canna-Club" icon={Cannabis} indent />
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Navigation Bottom */}
          <NavLink path="/pricing" label="Join Our Network!" icon={CreditCard} />
          <NavLink path="/about" label="About Us" icon={Info} />
          <NavLink path="/contact" label="Contact" icon={MessageSquare} />

          {/* User Section */}
          <div className="mt-4 pt-4 border-t border-border/50">
            {loading ? (
              <div className="h-10 bg-muted animate-pulse rounded-lg" />
            ) : user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="w-full justify-start gap-2 h-auto py-2" data-testid="user-menu-trigger">
                    {user.picture ? (
                      <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full" />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-purple-500/20 flex items-center justify-center">
                        <User size={16} className="text-purple-400" />
                      </div>
                    )}
                    <div className="text-left">
                      <p className="text-sm font-medium truncate max-w-[120px]">{user.name}</p>
                      <p className="text-[10px] text-gray-500 capitalize">{user.role}</p>
                    </div>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-48">
                  <Link to={user ? `/u/${user.id}` : "/profile"} onClick={() => setIsOpen(false)}>
                    <DropdownMenuItem>
                      <User size={14} className="mr-2" />
                      My Profile
                    </DropdownMenuItem>
                  </Link>
                  {user.role === 'admin' && (
                    <Link to="/admin" onClick={() => setIsOpen(false)}>
                      <DropdownMenuItem>
                        <LayoutDashboard size={14} className="mr-2" />
                        Admin Panel
                        {pendingCount > 0 && (
                          <span className="ml-auto bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
                            {pendingCount > 99 ? '99+' : pendingCount}
                          </span>
                        )}
                      </DropdownMenuItem>
                    </Link>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={logout} data-testid="logout-btn">
                    <LogOut size={14} className="mr-2" />
                    Sign Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Button
                onClick={login}
                variant="outline"
                size="sm"
                className="w-full gap-2"
                data-testid="login-btn"
              >
                <LogIn size={16} />
                Sign In
              </Button>
            )}
          </div>
        </nav>

        {/* Footer */}
        <div className="shrink-0 p-3 border-t border-border/50 space-y-2">
          {!isInstalled && (
            <button
              onClick={handleInstallClick}
              className="flex items-center justify-center gap-1.5 w-full py-2 text-xs text-purple-400 hover:text-purple-300 hover:bg-purple-500/10 rounded-lg transition-colors border border-purple-500/30"
              data-testid="install-app-btn"
            >
              <Download size={12} />
              Install App
            </button>
          )}
          
          <button
            onClick={handleForceUpdate}
            disabled={isUpdating}
            className="flex items-center justify-center gap-1.5 w-full py-2 text-xs text-gray-500 hover:text-white hover:bg-white/5 rounded-lg transition-colors disabled:opacity-50"
            data-testid="update-app-btn"
          >
            <RefreshCw size={12} className={isUpdating ? 'animate-spin' : ''} />
            {isUpdating ? 'Updating...' : 'Update App'}
          </button>
          <p className="text-[8px] text-gray-600 text-center mt-2">
            Powered by Mystery Machine Productions
          </p>
        </div>
      </aside>

      {/* iOS Install Instructions Modal */}
      <AnimatePresence>
        {showIOSInstructions && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 z-[60] flex items-center justify-center p-4"
            onClick={() => setShowIOSInstructions(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-card border border-border rounded-xl p-6 max-w-sm w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center">
                  <Smartphone size={20} className="text-purple-400" />
                </div>
                <h3 className="font-heading font-bold text-lg">Install on iOS</h3>
              </div>
              
              <ol className="space-y-3 text-sm text-muted-foreground mb-4">
                <li className="flex gap-2">
                  <span className="font-bold text-purple-400">1.</span>
                  Tap the <span className="text-white font-medium">Share</span> button (square with arrow)
                </li>
                <li className="flex gap-2">
                  <span className="font-bold text-purple-400">2.</span>
                  Scroll down and tap <span className="text-white font-medium">"Add to Home Screen"</span>
                </li>
                <li className="flex gap-2">
                  <span className="font-bold text-purple-400">3.</span>
                  Tap <span className="text-white font-medium">"Add"</span> in the top right
                </li>
              </ol>
              
              <Button 
                onClick={() => setShowIOSInstructions(false)}
                className="w-full"
              >
                Got it!
              </Button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Sidebar;
